import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { FileDoc, Printer, ArrowLeft } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const WorkflowGuidePage = () => {
  const navigate = useNavigate();

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Print-hidden header */}
      <div className="print:hidden bg-[#4A6C4C] text-white p-4 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/')} className="text-white hover:bg-white/20">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to App
          </Button>
          <h1 className="font-medium">GinsGood Workflow Guide</h1>
          <Button onClick={handlePrint} className="bg-white text-[#4A6C4C] hover:bg-white/90">
            <Printer className="w-4 h-4 mr-2" />
            Print / Save as PDF
          </Button>
        </div>
      </div>

      {/* Document Content */}
      <div className="max-w-4xl mx-auto p-8 print:p-4">
        {/* Title Page */}
        <div className="text-center mb-12 print:mb-8">
          <div className="w-20 h-20 mx-auto mb-4 bg-[#4A6C4C] rounded-full flex items-center justify-center">
            <FileDoc className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-[#4A6C4C] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            GinsGood Platform
          </h1>
          <h2 className="text-2xl text-gray-600 mb-4">Complete User Workflow Guide</h2>
          <p className="text-gray-500">Version 2.0 | March 2026</p>
          <div className="mt-6 p-4 bg-green-50 rounded-lg inline-block">
            <p className="font-medium text-[#4A6C4C]">Live App URL:</p>
            <p className="text-lg">https://brand-verify-7.preview.emergentagent.com</p>
          </div>
        </div>

        {/* Table of Contents */}
        <Card className="p-6 mb-8 print:break-after-page">
          <h2 className="text-xl font-bold mb-4 text-[#4A6C4C]">Table of Contents</h2>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Getting Started & Registration</li>
            <li>Product Certification</li>
            <li>Digital Passport & Certificates</li>
            <li>GSG Wallet & Token Management</li>
            <li>Carbon Credits & Offset</li>
            <li>Achievements & Challenges</li>
            <li>Referral Program</li>
            <li>Supply Chain Tracking</li>
            <li>For Companies (B2B)</li>
            <li>Quick Reference URLs</li>
          </ol>
        </Card>

        {/* Section 1 */}
        <section className="mb-10 print:break-inside-avoid">
          <h2 className="text-2xl font-bold text-[#4A6C4C] mb-4 border-b-2 border-[#4A6C4C] pb-2">
            1. Getting Started & Registration
          </h2>
          
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="font-medium">Go to: <span className="font-mono bg-white px-2 py-1 rounded">/iris-auth</span></p>
          </div>

          <h3 className="font-bold text-lg mb-2">Steps to Register:</h3>
          <ol className="list-decimal list-inside space-y-2 mb-4 ml-4">
            <li>Click "Certify" in the top menu, then "Register Identity"</li>
            <li>Enter your <strong>Full Name</strong></li>
            <li>Enter your <strong>Email Address</strong></li>
            <li>Enter your <strong>Phone Number</strong></li>
            <li>Select your <strong>User Type</strong>:
              <ul className="list-disc list-inside ml-6 mt-1">
                <li>Seller - If you sell products</li>
                <li>Artisan - If you make handcrafted items</li>
                <li>Customer - If you buy certified products</li>
              </ul>
            </li>
            <li><em>(Optional)</em> Enter a <strong>Referral Code</strong> to get bonus coins</li>
            <li>Click <strong>"Create Digital Identity"</strong></li>
          </ol>

          <div className="bg-green-50 p-4 rounded-lg">
            <p className="font-medium text-green-800">✓ What you receive:</p>
            <ul className="list-disc list-inside ml-4 text-green-700">
              <li>A unique digital identity (Iris ID)</li>
              <li>A GSG Wallet for loyalty coins</li>
              <li>A Solana blockchain wallet</li>
              <li>+100 GSG bonus if you used a referral code</li>
            </ul>
          </div>
        </section>

        {/* Section 2 */}
        <section className="mb-10 print:break-inside-avoid">
          <h2 className="text-2xl font-bold text-[#4A6C4C] mb-4 border-b-2 border-[#4A6C4C] pb-2">
            2. Product Certification
          </h2>

          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="font-medium">Go to: <span className="font-mono bg-white px-2 py-1 rounded">/certify</span></p>
          </div>

          <h3 className="font-bold text-lg mb-2">Steps to Certify a Product:</h3>
          <ol className="list-decimal list-inside space-y-2 mb-4 ml-4">
            <li><strong>Select an Industry Template</strong>:
              <ul className="list-disc list-inside ml-6 mt-1">
                <li>Fashion & Apparel</li>
                <li>Artisanal Crafts</li>
                <li>Sustainable Textiles</li>
                <li>Organic Products</li>
                <li>Jewelry & Accessories</li>
                <li>Eco Beauty</li>
              </ul>
            </li>
            <li><strong>Enter Product Details</strong>:
              <ul className="list-disc list-inside ml-6 mt-1">
                <li>Product Name</li>
                <li>Brand or Company Name</li>
                <li>Description</li>
                <li>Category</li>
                <li>Price</li>
                <li>Country of Origin</li>
              </ul>
            </li>
            <li><strong>Answer the Sustainability Questionnaire</strong> - Questions about materials, labor practices, environmental impact</li>
            <li><strong>Review Your Score</strong> and submit</li>
          </ol>

          <h3 className="font-bold text-lg mb-2">Certification Tiers & Rewards:</h3>
          <table className="w-full border-collapse border border-gray-300 mb-4">
            <thead className="bg-gray-100">
              <tr>
                <th className="border border-gray-300 p-2 text-left">Tier</th>
                <th className="border border-gray-300 p-2 text-left">Score Range</th>
                <th className="border border-gray-300 p-2 text-left">GSG Reward</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className="border border-gray-300 p-2">Platinum</td><td className="border border-gray-300 p-2">90-100</td><td className="border border-gray-300 p-2">+200 GSG</td></tr>
              <tr><td className="border border-gray-300 p-2">Gold</td><td className="border border-gray-300 p-2">75-89</td><td className="border border-gray-300 p-2">+100 GSG</td></tr>
              <tr><td className="border border-gray-300 p-2">Silver</td><td className="border border-gray-300 p-2">50-74</td><td className="border border-gray-300 p-2">+50 GSG</td></tr>
              <tr><td className="border border-gray-300 p-2">Bronze</td><td className="border border-gray-300 p-2">25-49</td><td className="border border-gray-300 p-2">-</td></tr>
            </tbody>
          </table>

          <div className="bg-yellow-50 p-4 rounded-lg">
            <p className="font-medium text-yellow-800">⭐ Bonus: Your first certification earns +500 GSG!</p>
          </div>
        </section>

        {/* Section 3 */}
        <section className="mb-10 print:break-inside-avoid">
          <h2 className="text-2xl font-bold text-[#4A6C4C] mb-4 border-b-2 border-[#4A6C4C] pb-2">
            3. Digital Passport & Certificates
          </h2>

          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="font-medium">Go to: <span className="font-mono bg-white px-2 py-1 rounded">/passport/[certificate-id]</span></p>
          </div>

          <p className="mb-4">After certifying a product, you receive a Digital Passport with:</p>

          <ul className="list-disc list-inside space-y-2 mb-4 ml-4">
            <li><strong>Certificate Details</strong> - Product name, brand, score, tier badge</li>
            <li><strong>QR Code</strong> - Scannable code for verification</li>
            <li><strong>Blockchain Verification</strong> - Proof recorded on Solana blockchain</li>
          </ul>

          <h3 className="font-bold text-lg mb-2">Actions You Can Take:</h3>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li><strong>Download PDF</strong> - Save a professional certificate document</li>
            <li><strong>Mint as NFT</strong> - Create a blockchain NFT of your certificate</li>
            <li><strong>View Supply Chain</strong> - See the product's journey</li>
            <li><strong>Share</strong> - Post on social media</li>
          </ul>
        </section>

        {/* Section 4 */}
        <section className="mb-10 print:break-inside-avoid">
          <h2 className="text-2xl font-bold text-[#4A6C4C] mb-4 border-b-2 border-[#4A6C4C] pb-2">
            4. GSG Wallet & Token Management
          </h2>

          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="font-medium">Go to: <span className="font-mono bg-white px-2 py-1 rounded">/wallet</span></p>
          </div>

          <p className="mb-4">Your wallet has 5 tabs:</p>

          <div className="space-y-4">
            <div className="border-l-4 border-green-500 pl-4">
              <h4 className="font-bold">Tab 1: Earn</h4>
              <p>Shows all the ways you can earn GSG coins</p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-bold">Tab 2: Carbon ⭐ (Important!)</h4>
              <p>Manage your carbon tokens - see Section 5 for details</p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h4 className="font-bold">Tab 3: Redeem</h4>
              <p>Spend your GSG on rewards and discounts</p>
            </div>

            <div className="border-l-4 border-orange-500 pl-4">
              <h4 className="font-bold">Tab 4: History</h4>
              <p>View all your past transactions</p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4">
              <h4 className="font-bold">Tab 5: Leaders</h4>
              <p>See the top GSG earners</p>
            </div>
          </div>
        </section>

        {/* Section 5 */}
        <section className="mb-10 print:break-inside-avoid">
          <h2 className="text-2xl font-bold text-[#4A6C4C] mb-4 border-b-2 border-[#4A6C4C] pb-2">
            5. Carbon Credits & Offset
          </h2>

          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="font-medium">Go to: <span className="font-mono bg-white px-2 py-1 rounded">/wallet</span> → Click "Carbon" tab</p>
          </div>

          <h3 className="font-bold text-lg mb-2">Step 1: Setup Token Accounts</h3>
          <p className="mb-4 ml-4">Click the <strong>"Setup Token Accounts"</strong> button. This creates your blockchain wallet for carbon tokens.</p>

          <h3 className="font-bold text-lg mb-2">Step 2: Exchange GSG for Carbon Credits (GCC)</h3>
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <p className="font-medium mb-2">Exchange Rate:</p>
            <p className="text-2xl font-bold text-center text-[#4A6C4C]">1,000 GSG = 1 GCC = 1 kg CO2 offset</p>
            <p className="text-center text-sm text-gray-600 mt-2">Minimum exchange: 100 GSG</p>
          </div>
          <ol className="list-decimal list-inside space-y-2 mb-4 ml-4">
            <li>Enter the amount of GSG you want to exchange</li>
            <li>See how many GCC you'll receive</li>
            <li>Click <strong>"Exchange"</strong></li>
          </ol>

          <h3 className="font-bold text-lg mb-2">Step 3: Retire Carbon Credits</h3>
          <p className="mb-2 ml-4">Retiring carbon credits means permanently offsetting carbon emissions:</p>
          <ol className="list-decimal list-inside space-y-2 mb-4 ml-4">
            <li>Enter the amount of GCC to retire</li>
            <li>Click <strong>"Retire & Offset"</strong></li>
            <li>Receive your carbon offset certificate</li>
          </ol>

          <div className="bg-green-50 p-4 rounded-lg">
            <p className="font-medium text-green-800">What Your Offset Equals:</p>
            <ul className="list-disc list-inside ml-4 text-green-700">
              <li>1 kg CO2 = 0.05 trees planted per year</li>
              <li>1 kg CO2 = 4.8 km not driven</li>
              <li>1 kg CO2 = 167 LED bulb hours saved</li>
            </ul>
          </div>
        </section>

        {/* Section 6 */}
        <section className="mb-10 print:break-inside-avoid">
          <h2 className="text-2xl font-bold text-[#4A6C4C] mb-4 border-b-2 border-[#4A6C4C] pb-2">
            6. Achievements & Challenges
          </h2>

          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="font-medium">Go to: <span className="font-mono bg-white px-2 py-1 rounded">/achievements</span></p>
          </div>

          <h3 className="font-bold text-lg mb-2">Available Achievements (10 Total):</h3>
          <table className="w-full border-collapse border border-gray-300 mb-4 text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="border border-gray-300 p-2 text-left">Achievement</th>
                <th className="border border-gray-300 p-2 text-left">How to Earn</th>
                <th className="border border-gray-300 p-2 text-left">Reward</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className="border border-gray-300 p-2">First Step</td><td className="border border-gray-300 p-2">Retire your first carbon credit</td><td className="border border-gray-300 p-2">+50 GSG</td></tr>
              <tr><td className="border border-gray-300 p-2">Tree Planter</td><td className="border border-gray-300 p-2">Offset 20 kg CO2</td><td className="border border-gray-300 p-2">+100 GSG</td></tr>
              <tr><td className="border border-gray-300 p-2">Forest Guardian</td><td className="border border-gray-300 p-2">Offset 100 kg CO2</td><td className="border border-gray-300 p-2">+500 GSG</td></tr>
              <tr><td className="border border-gray-300 p-2">Carbon Warrior</td><td className="border border-gray-300 p-2">Complete 10 retirements</td><td className="border border-gray-300 p-2">+200 GSG</td></tr>
              <tr><td className="border border-gray-300 p-2">Climate Champion</td><td className="border border-gray-300 p-2">Offset 1,000 kg CO2</td><td className="border border-gray-300 p-2">+2,000 GSG</td></tr>
              <tr><td className="border border-gray-300 p-2">Week Warrior</td><td className="border border-gray-300 p-2">7-day activity streak</td><td className="border border-gray-300 p-2">+100 GSG</td></tr>
              <tr><td className="border border-gray-300 p-2">Monthly Momentum</td><td className="border border-gray-300 p-2">30-day activity streak</td><td className="border border-gray-300 p-2">+500 GSG</td></tr>
              <tr><td className="border border-gray-300 p-2">Exchange Master</td><td className="border border-gray-300 p-2">Exchange 5,000 GSG</td><td className="border border-gray-300 p-2">+150 GSG</td></tr>
              <tr><td className="border border-gray-300 p-2">Referral Champion</td><td className="border border-gray-300 p-2">5 referrals who offset carbon</td><td className="border border-gray-300 p-2">+300 GSG</td></tr>
              <tr><td className="border border-gray-300 p-2">Early Adopter</td><td className="border border-gray-300 p-2">Be among first 100 users</td><td className="border border-gray-300 p-2">+200 GSG</td></tr>
            </tbody>
          </table>

          <h3 className="font-bold text-lg mb-2">Weekly Challenges (Reset Every Monday):</h3>
          <ul className="list-disc list-inside space-y-1 mb-4 ml-4">
            <li>Retire 5 kg carbon → +75 GSG</li>
            <li>Exchange 1,000 GSG → +50 GSG</li>
          </ul>

          <h3 className="font-bold text-lg mb-2">Monthly Challenges (Reset on 1st):</h3>
          <ul className="list-disc list-inside space-y-1 mb-4 ml-4">
            <li>Retire 25 kg carbon → +200 GSG</li>
            <li>Be active 10 different days → +150 GSG</li>
          </ul>

          <h3 className="font-bold text-lg mb-2">Share Your Impact:</h3>
          <p className="ml-4">Click the "Share" tab to post your achievements on Twitter and LinkedIn!</p>
        </section>

        {/* Section 7 */}
        <section className="mb-10 print:break-inside-avoid">
          <h2 className="text-2xl font-bold text-[#4A6C4C] mb-4 border-b-2 border-[#4A6C4C] pb-2">
            7. Referral Program
          </h2>

          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="font-medium">Go to: <span className="font-mono bg-white px-2 py-1 rounded">/referral</span></p>
          </div>

          <h3 className="font-bold text-lg mb-2">How to Refer Friends:</h3>
          <ol className="list-decimal list-inside space-y-2 mb-4 ml-4">
            <li>Visit the Referral page</li>
            <li>Copy your unique referral code (e.g., <strong>GSG-SELL-ZUWKG8</strong>)</li>
            <li>Share it with friends</li>
            <li>They enter your code when registering</li>
            <li>You both earn rewards!</li>
          </ol>

          <h3 className="font-bold text-lg mb-2">Referral Rewards:</h3>
          <table className="w-full border-collapse border border-gray-300 mb-4">
            <thead className="bg-gray-100">
              <tr>
                <th className="border border-gray-300 p-2 text-left">Who Registers</th>
                <th className="border border-gray-300 p-2 text-left">You Earn</th>
                <th className="border border-gray-300 p-2 text-left">They Earn</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className="border border-gray-300 p-2">Company</td><td className="border border-gray-300 p-2">+2,000 GSG</td><td className="border border-gray-300 p-2">+500 GSG</td></tr>
              <tr><td className="border border-gray-300 p-2">Seller</td><td className="border border-gray-300 p-2">+500 GSG</td><td className="border border-gray-300 p-2">+100 GSG</td></tr>
              <tr><td className="border border-gray-300 p-2">Customer</td><td className="border border-gray-300 p-2">+200 GSG</td><td className="border border-gray-300 p-2">+50 GSG</td></tr>
            </tbody>
          </table>
        </section>

        {/* Section 8 */}
        <section className="mb-10 print:break-inside-avoid">
          <h2 className="text-2xl font-bold text-[#4A6C4C] mb-4 border-b-2 border-[#4A6C4C] pb-2">
            8. Supply Chain Tracking
          </h2>

          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="font-medium">Access: View any product passport → Click "Supply Chain"</p>
          </div>

          <p className="mb-4">Track a product's journey through 5 stages:</p>

          <ol className="list-decimal list-inside space-y-2 mb-4 ml-4">
            <li><strong>Raw Material</strong> - Where materials come from</li>
            <li><strong>Processing</strong> - How materials are processed</li>
            <li><strong>Manufacturing</strong> - How the product is made</li>
            <li><strong>Packaging</strong> - Sustainable packaging used</li>
            <li><strong>Distribution</strong> - How it reaches customers</li>
          </ol>

          <p>Each stage is verified on the blockchain for transparency.</p>
        </section>

        {/* Section 9 */}
        <section className="mb-10 print:break-inside-avoid">
          <h2 className="text-2xl font-bold text-[#4A6C4C] mb-4 border-b-2 border-[#4A6C4C] pb-2">
            9. For Companies (B2B)
          </h2>

          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="font-medium">Register: <span className="font-mono bg-white px-2 py-1 rounded">/company/register</span></p>
            <p className="font-medium">Dashboard: <span className="font-mono bg-white px-2 py-1 rounded">/company/dashboard</span></p>
          </div>

          <h3 className="font-bold text-lg mb-2">Company Features:</h3>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li><strong>API Access</strong> - Integrate certification into your systems</li>
            <li><strong>Usage Monitoring</strong> - Track how many certifications you've issued</li>
            <li><strong>Webhook Notifications</strong> - Get notified when events happen</li>
            <li><strong>White-Label Options</strong> - Brand the certification experience</li>
          </ul>
        </section>

        {/* Section 10 */}
        <section className="mb-10 print:break-inside-avoid">
          <h2 className="text-2xl font-bold text-[#4A6C4C] mb-4 border-b-2 border-[#4A6C4C] pb-2">
            10. Quick Reference - All Pages
          </h2>

          <table className="w-full border-collapse border border-gray-300">
            <thead className="bg-gray-100">
              <tr>
                <th className="border border-gray-300 p-2 text-left">Page</th>
                <th className="border border-gray-300 p-2 text-left">URL</th>
                <th className="border border-gray-300 p-2 text-left">What It Does</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className="border border-gray-300 p-2">Home</td><td className="border border-gray-300 p-2 font-mono text-sm">/</td><td className="border border-gray-300 p-2">Main landing page</td></tr>
              <tr><td className="border border-gray-300 p-2">Register</td><td className="border border-gray-300 p-2 font-mono text-sm">/iris-auth</td><td className="border border-gray-300 p-2">Create your account</td></tr>
              <tr><td className="border border-gray-300 p-2">Certify</td><td className="border border-gray-300 p-2 font-mono text-sm">/certify</td><td className="border border-gray-300 p-2">Certify products</td></tr>
              <tr><td className="border border-gray-300 p-2">Products</td><td className="border border-gray-300 p-2 font-mono text-sm">/products</td><td className="border border-gray-300 p-2">Browse certified products</td></tr>
              <tr><td className="border border-gray-300 p-2">Wallet</td><td className="border border-gray-300 p-2 font-mono text-sm">/wallet</td><td className="border border-gray-300 p-2">Manage GSG & carbon tokens</td></tr>
              <tr><td className="border border-gray-300 p-2">Carbon</td><td className="border border-gray-300 p-2 font-mono text-sm">/carbon</td><td className="border border-gray-300 p-2">View carbon offset stats</td></tr>
              <tr><td className="border border-gray-300 p-2">Achievements</td><td className="border border-gray-300 p-2 font-mono text-sm">/achievements</td><td className="border border-gray-300 p-2">Badges & challenges</td></tr>
              <tr><td className="border border-gray-300 p-2">Referral</td><td className="border border-gray-300 p-2 font-mono text-sm">/referral</td><td className="border border-gray-300 p-2">Refer friends</td></tr>
              <tr><td className="border border-gray-300 p-2">Auditors</td><td className="border border-gray-300 p-2 font-mono text-sm">/auditors</td><td className="border border-gray-300 p-2">Third-party verification</td></tr>
              <tr><td className="border border-gray-300 p-2">Supply Chain</td><td className="border border-gray-300 p-2 font-mono text-sm">/supply-chain</td><td className="border border-gray-300 p-2">Product journey tracking</td></tr>
              <tr><td className="border border-gray-300 p-2">QR Scanner</td><td className="border border-gray-300 p-2 font-mono text-sm">/scan</td><td className="border border-gray-300 p-2">Scan product QR codes</td></tr>
            </tbody>
          </table>
        </section>

        {/* Test Credentials */}
        <section className="mb-10 print:break-inside-avoid">
          <div className="bg-yellow-50 border-2 border-yellow-300 p-6 rounded-lg">
            <h2 className="text-xl font-bold text-yellow-800 mb-4">🧪 Test Credentials</h2>
            <table className="w-full">
              <tbody>
                <tr>
                  <td className="font-medium py-1">Test Referral Code:</td>
                  <td className="font-mono bg-white px-2 py-1 rounded">GSG-SELL-ZUWKG8</td>
                </tr>
                <tr>
                  <td className="font-medium py-1">Example Passport:</td>
                  <td className="font-mono bg-white px-2 py-1 rounded text-sm">/passport/ad5c06b0-5a1d-4c04-9945-eac5b7b81a2e</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm border-t pt-6">
          <p>GinsGood Platform - Sustainability Certification</p>
          <p>Version 2.0 | Last Updated: March 2026</p>
          <p className="mt-2">https://brand-verify-7.preview.emergentagent.com</p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden { display: none !important; }
          .print\\:break-after-page { break-after: page; }
          .print\\:break-inside-avoid { break-inside: avoid; }
          .print\\:p-4 { padding: 1rem !important; }
          .print\\:mb-8 { margin-bottom: 2rem !important; }
        }
      `}</style>
    </div>
  );
};

export default WorkflowGuidePage;
